import { Box, css, Stack, styled } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, useLayoutEffect, useMemo, useRef } from 'react';
import { ChatSocketMessageType } from '../../../schemas/agent-conversation.schema';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { useMessageFeedback } from '../hooks/useMessageFeedback';
import { isLastAssistantMessageInSequence, isMessagePersistedAndRatable } from '../utils/feedbackUtils';
import { useAtomValue } from 'jotai';
import { AgentActivityAtom, pickActivityLabel } from '../hooks/agentActivityAtom';
import { ChatFollowBottomContext, useFollowBottom } from '../hooks/useFollowBottom';
import { ActivityIndicator } from './ActivityIndicator';
import { AnimatedChatbotIcon } from './AnimatedChatbotIcon';
import { FeedbackProps } from './FeedbackButtons';
import { Message, MessageData } from './Message';
import { ScrollToBottom } from './ScrollButton';
import { ChatBubblePaddingY, ChatParagraphGap } from './style-defs';

const ScrollableContainer = css`
  /* overscroll-behavior: contain; */
  height: 100%;
  max-height: 100%;
  min-height: 100%;
  padding-top: 1.5rem;
  overflow-y: auto;
  scroll-behavior: smooth;
  scroll-padding-bottom: 3rem;
  display: grid;
  align-items: start;
  /* Lets the latest turn size itself against this viewport (100cqh). Also
     makes this a containing block for any fixed descendant — nothing here is
     fixed today (MUI portals out). */
  container-type: size;

  &:focus-visible {
    outline: 2px solid var(--mui-palette-primary-light);
    outline-offset: -2px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--mui-palette-grey-300);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--mui-palette-grey-400);
  }
`;

const MessagesRowGap = 6;
const MessagePaddingY = 2;
const MessageContainer = styled(Box)`
  display: grid;
  /* Grid items default to min-width: auto, so one message carrying an unbroken
     token sizes the whole column to it and every line clips at the panel edge. */
  min-width: 0;

  > * {
    min-width: 0;
  }
  row-gap: ${({ theme }) => theme.spacing(MessagesRowGap)};
  padding: ${({ theme }) => theme.spacing(MessagePaddingY, 3)};

  /* One logical answer can persist as several consecutive bot rows, so the
     gap between them collapses to the in-reply paragraph gap and they read
     as one bubble. Cross-source neighbors (e.g. an Atlas digest pushed
     mid-turn) keep the gap — the flag is computed per message in the render
     loop, since CSS sibling selectors can't compare attribute values. */
  [data-collapse-with-previous='true'] {
    margin-top: calc(
      ${ChatParagraphGap} - ${({ theme }) => theme.spacing(MessagesRowGap + 2 * ChatBubblePaddingY)}
    );
  }
`;

/* Every turn (a question through its replies) renders in its own wrapper so
 * messages never move between parents — a reparent would remount the previous
 * reply's editors and shift the layout under the send-scroll. Only the latest
 * turn reserves a viewport of height, so sending scrolls the question to the
 * top and the reply fills the room beneath it before any scrolling is needed.
 * The room stays under a short final answer until the next turn, as in Claude. */
const Turn = styled(Box)`
  display: grid;
  align-content: start;
  row-gap: ${({ theme }) => theme.spacing(MessagesRowGap)};

  > * {
    min-width: 0;
  }

  &[data-last-turn='true'] {
    /* 100cqh is the scroller's content box (excludes its 1.5rem padding-top);
       the 2rem is what lands the question at the top with a little air. */
    min-height: calc(100cqh - 2rem);
  }
`;

interface MessageTurn {
  key: string;
  start: number;
  messages: MessageData[];
}

/** Split the list into turns: each user message opens a new one; anything
 *  before the first user message forms a leading turn. */
function groupIntoTurns(messages: MessageData[]): MessageTurn[] {
  const turns: MessageTurn[] = [];
  messages.forEach((message, index) => {
    const current = turns.at(-1);
    if (message.isUser || current === undefined) {
      turns.push({ key: message.clientKey, start: index, messages: [message] });
    } else {
      current.messages.push(message);
    }
  });
  return turns;
}

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  padding-bottom: 3dvh;
  flex: 1;
  height: 100%;
  gap: ${({ theme }) => theme.spacing(3)};

  img {
    width: 130px;
    height: 130px;
  }
`;
const EmptyStateText = styled(Box)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 1.4rem;
  font-weight: 500;
  text-align: center;
  animation: slideInFromRight 1s ease-out;
  animation-delay: 0.5s;
  animation-fill-mode: both;

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  margin-top: 0.75rem;
`;
const EmptyStateText2 = styled(EmptyStateText)`
  animation-delay: 1s;
  margin-top: 0;
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(60px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

interface MessageListProps {
  messages: MessageData[];
  isTyping?: boolean;
}

export const MessageList: FC<MessageListProps> = ({ messages, isTyping = false }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { showAgentFeedback } = useFlags<MaggieFeatureFlags>();
  const { submitFeedback, isPending: isFeedbackPending } = useMessageFeedback();
  // Ambient background-agent activity (e.g. "Atlas: running transform") shows
  // in the same bottom-line spot even when no generation turn is in flight.
  const hasActivityLabel = pickActivityLabel(useAtomValue(AgentActivityAtom)) !== undefined;
  // Streaming bubbles call followBottom (via context) after each slice commits;
  // it only scrolls while the reader hasn't scrolled away themselves.
  const {
    onScroll,
    onWheel,
    onTouchStart,
    onTouchMove,
    onKeyDown,
    onPointerDown,
    followBottom,
    resumeFollowing,
  } = useFollowBottom(scrollRef);

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const lastMessage = messages.at(-1);
  const lastUserKey = lastMessage?.isUser ? lastMessage.clientKey : undefined;
  useLayoutEffect(() => {
    // Sending always jumps to the bottom and re-arms following for the reply —
    // keyed on the message itself so back-to-back sends (a retry after an
    // error left the question last) fire too.
    if (lastUserKey !== undefined) {
      resumeFollowing();
      scrollToBottom();
    }
  }, [lastUserKey, resumeFollowing]);

  const innerContent = useMemo(() => {
    if (messages.length === 0 && !isTyping) {
      return (
        <Stack direction='column' justifyContent='flex-end' height='100%'>
          <EmptyState>
            <AnimatedChatbotIcon />
            <EmptyStateText>Ask any question</EmptyStateText>
            <EmptyStateText2>about your data</EmptyStateText2>
          </EmptyState>
          {/* Ambient background-agent activity (e.g. Atlas mid-engagement) must
              stay visible even before the first message — the Atlas console
              auto-opens an empty chat. */}
          {hasActivityLabel && (
            <Box ml={4} mb={2}>
              <ActivityIndicator />
            </Box>
          )}
        </Stack>
      );
    }

    const turns = groupIntoTurns(messages);

    const renderMessage = (message: MessageData, idx: number) => {
      // Chunk-typed bubbles are still streaming — "is the last message"
      // would misattribute the stream when an out-of-band message (e.g. an
      // Atlas digest) is inserted behind it mid-turn.
      const isMessageStreaming =
        !message.isUser && isTyping && message.type === ChatSocketMessageType.aiResponseChunk;

      // Same-source bot neighbors fuse into one visual bubble: one event
      // turn can persist several consecutive assistant rows (FD-4182), and
      // they must read as one reply just like a normal turn's chunks.
      // Cross-source neighbors keep the gap — an unprompted reply landing
      // right after the agent's answer must not read as paragraphs
      // silently appended to it.
      const previousMessage = idx > 0 ? messages[idx - 1] : undefined;
      const collapseWithPrevious =
        !message.isUser &&
        previousMessage !== undefined &&
        !previousMessage.isUser &&
        message.source === previousMessage.source;

      const shouldShowFeedback =
        showAgentFeedback &&
        !message.isUser &&
        isMessagePersistedAndRatable(message.id as number) &&
        isLastAssistantMessageInSequence(messages, idx) &&
        !isMessageStreaming;

      const feedbackProps: FeedbackProps | undefined = shouldShowFeedback
        ? {
            messageId: message.id as number,
            feedback: message.feedback ?? null,
            onFeedback: submitFeedback,
            disabled: isFeedbackPending,
          }
        : undefined;

      return (
        <div
          key={message.clientKey}
          data-is-user={message.isUser}
          data-source={message.source}
          data-collapse-with-previous={collapseWithPrevious}
        >
          <Message
            content={message.content}
            isUser={message.isUser}
            animate={isMessageStreaming}
            feedbackProps={feedbackProps}
          />
        </div>
      );
    };

    return (
      <MessageContainer>
        {turns.map((turn, turnIndex) => {
          const isLastTurn = turnIndex === turns.length - 1;
          return (
            <Turn key={turn.key} data-last-turn={isLastTurn && turn.messages[0].isUser}>
              {turn.messages.map((message, offset) => renderMessage(message, turn.start + offset))}
              {/* Claude-Desktop-style status line, always pinned after the last
                  message just above the input: the sparkle alone while idle,
                  sparkle + label during a tool call (successive agent_activity
                  labels replace each other in the atom, last-write-wins),
                  sparkle + "Thinking..." shimmer otherwise while a turn is in
                  flight. Idle ambient activity (e.g. Atlas) shows here too. */}
              {isLastTurn && (
                <Box ml={4}>
                  <ActivityIndicator isTyping={isTyping} />
                </Box>
              )}
            </Turn>
          );
        })}
        <div ref={bottomRef} />
      </MessageContainer>
    );
  }, [messages, isTyping, submitFeedback, isFeedbackPending, showAgentFeedback, hasActivityLabel]);

  const key = messages.length > 0 ? `1-${messages.at(0)?.id ?? ''}` : '0';

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        css={ScrollableContainer}
        ref={scrollRef}
        key={key}
        onScroll={onScroll}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        // Focusable so keyboard users can scroll (and pause following) here.
        tabIndex={0}
        role='region'
        aria-label='Conversation'
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{
          duration: 0.5,
        }}
      >
        <ChatFollowBottomContext.Provider value={followBottom}>
          {innerContent}
        </ChatFollowBottomContext.Provider>
      </motion.div>
      <ScrollToBottom containerRef={scrollRef} />
    </AnimatePresence>
  );
};
